// The one list of the body's parts, given to the editor through its extension
// slot (`createEditorExtensions({ extensions })` replaces a built-in node of
// the same name; schema and markdown stay the editor's).
import type { AnyExtension } from "@tiptap/core";
import type { Api } from "../../data/api";
import type { DocumentBlockContext, DocumentBlockPart } from "./block-parts";
import {
  CalloutTitleWithPlaceholder,
  CalloutWithKindMenu,
} from "./callout-view";
import { CodeBlockWithHighlight, codeBlockWithParts } from "./code-highlight";
import { DetailsSummaryWithToggle, DetailsWithToggle } from "./details-toggle";
import { FenceWithView } from "./fence-view";
import { FileUpload } from "./file-upload";
import { FileInlineWithView, FileWithView } from "./file-view";
import { FootnoteDefWithView, FootnoteRefWithView } from "./footnote-view";
import { TableWithControls } from "./table-view";

const NO_ANSWERS: DocumentBlockContext = { answersFor: () => undefined };

/**
 * How the body's blocks look, without the live behaviours (footnote dialogs,
 * uploads): for editors inside a document's parts (the footnote dialog).
 */
export function displayBlockViews(): AnyExtension[] {
  return [
    TableWithControls,
    FileWithView,
    FileInlineWithView,
    CalloutWithKindMenu,
    CalloutTitleWithPlaceholder,
    DetailsWithToggle,
    DetailsSummaryWithToggle,
    CodeBlockWithHighlight,
    FenceWithView,
  ];
}

/**
 * The document body's parts: every view above, footnotes with their dialog,
 * and files put in by paste, drop or `insertFiles` / `pickFiles` (through
 * `api`'s files REST). Code blocks take the views of the listed `parts` for
 * their languages (block-parts.ts). Mount `BlockLayers` next to the editor for
 * the dialogs, meters and viewers.
 */
export function documentBlockViews({
  api,
  parts = [],
  ctx = NO_ANSWERS,
}: {
  api: Api;
  parts?: readonly DocumentBlockPart[];
  ctx?: DocumentBlockContext;
}): AnyExtension[] {
  return [
    ...displayBlockViews().filter((ext) => ext !== CodeBlockWithHighlight),
    codeBlockWithParts(parts, ctx),
    FootnoteRefWithView,
    FootnoteDefWithView,
    FileUpload.configure({ api }),
  ];
}
